import React, { useState, useEffect } from 'react';
import { Form, Button, Modal, Table, Input } from 'semantic-ui-react';
import { useSelector, useDispatch } from 'react-redux';
import { subscriptionActions } from '../../actions';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import * as Yup from 'yup';
import './Invoice.css';

const regexMobile = /^09[0-9]{9}$/i;

const Invoice = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const status = useSelector(state => state.subscription.status);
  const error = useSelector(state => state.subscription.error);
  const [coupon, setCoupon] = useState('');
  const [couponError, setCouponError] = useState();

  const { invoice } = props;
  let discountAmount = 0;
  if (invoice.discount) {
    const { coupon } = invoice.discount;
    if (coupon.amountOff) {
      discountAmount += coupon.amountOff;
    }
    if (coupon.percentOff) {
      discountAmount += (coupon.percentOff * invoice.subscription.plan.amount) / 100;
    }
  }

  const interval = {
    day: t('day'),
    week: t('week'),
    month: t('month'),
    year: t('year'),
  };

  useEffect(() => {
    const errorName = _.get(error, 'name', null);
    if (
      errorName === 'NotFound' ||
      errorName === 'maxRedemptions' ||
      errorName === 'redeemBy' ||
      errorName === 'valid'
    ) {
      setCouponError({
        content: t('errorCouponInvalid'),
        pointing: 'above',
      });
    } else if (errorName === 'excessiveUse') {
      setCouponError({
        content: t('errorCouponExcessiveUse'),
        pointing: 'above',
      });
    } else if (error) {
      setCouponError({
        content: t('errorGlobalMessage'),
        pointing: 'above',
      });
    } else {
      setCouponError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const BillingInformation = Yup.object().shape({
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    mobile: Yup.string()
      .matches(new RegExp(regexMobile.source), t('yupMobileRegex'))
      .required(),
    email: Yup.string()
      .email(t('yupEmailValidate'))
      .required(),
    address: Yup.string().required(),
    postalCode: Yup.string().required(),
    country: Yup.string().required(),
    city: Yup.string().required(),
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      mobile: '',
      email: '',
      address: '',
      postalCode: '',
      country: '',
      city: '',
    },
    validationSchema: BillingInformation,
    onSubmit: values => {
      props.onPay(values);
    },
  });

  const useCoupon = () => {
    coupon &&
      coupon.length > 0 &&
      dispatch(
        subscriptionActions.useCoupon({
          couponCode: coupon,
          planId: invoice.subscription.plan._id,
        }),
      );
  };

  const pay = () => {
    if (invoice.currency === 'IRR') props.onPay();
    else {
      formik.handleSubmit();
    }
  };

  return (
    <Modal className="invoiceBox" open={props.open} closeOnDimmerClick={false}>
      <Modal.Header>{t('invoiceTitle')}</Modal.Header>
      <Modal.Content>
        <div className="detailsBox">
          <span className="message textCenter">{t('invoiceConfirmationMessage')} </span>
          <div className="transactionDetail">
            <div className="boxHeader">{t('paymentDetails')}</div>
            <div className="boxContent">
              <Table striped>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell>{t('subscriptionType')}</Table.Cell>
                    <Table.Cell className="bold">
                      {invoice.subscription.plan.intervalCount}{' '}
                      {interval[invoice.subscription.plan.interval]}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>{t('price')}</Table.Cell>
                    <Table.Cell className="bold">
                      {invoice.currency === 'IRR'
                        ? invoice.subtotal / 10 + ' ' + t('currencyToman')
                        : invoice.subtotal + ' EUR'}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>{t('discount')}</Table.Cell>
                    <Table.Cell className="bold">
                      {invoice.discount ? (
                        invoice.currency === 'IRR' ? (
                          discountAmount / 10 + ' ' + t('currencyToman')
                        ) : (
                          discountAmount + ' EUR'
                        )
                      ) : (
                        <Form>
                          <Form.Group widths="equal">
                            <Form.Field
                              control={Input}
                              disabled={status === 'using'}
                              onChange={event => setCoupon(event.target.value)}
                              error={couponError}
                            />
                            <Form.Field
                              control={Button}
                              loading={status === 'using'}
                              onClick={useCoupon}
                              content={t('apply')}
                            />
                          </Form.Group>
                        </Form>
                      )}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>{t('payableAmount')}</Table.Cell>
                    <Table.Cell className="bold">
                      {invoice.currency === 'IRR'
                        ? invoice.total / 10 + ' ' + t('currencyToman')
                        : invoice.total + ' EUR'}
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </div>
          </div>
          {invoice.currency !== 'IRR' ? (
            <div className="transactionDetail">
              <div className="boxHeader">{t('billingInformation')}</div>
              <div className="boxContent">
                <Form>
                  <Form.Group widths="equal">
                    <Form.Input
                      error={
                        _.has(formik.touched, 'firstName')
                          ? _.get(formik.errors, 'firstName')
                          : null
                      }
                      disabled={status === 'paying'}
                      fluid
                      type="text"
                      label={t('firstname')}
                      name={'firstName'}
                      placeholder={t('firstname')}
                      value={formik.values['firstName']}
                      onChange={formik.handleChange}
                    />
                    <Form.Input
                      error={
                        _.has(formik.touched, 'lastName') ? _.get(formik.errors, 'lastName') : null
                      }
                      disabled={status === 'paying'}
                      fluid
                      type="text"
                      label={t('lastname')}
                      name={'lastName'}
                      placeholder={t('lastname')}
                      value={formik.values['lastName']}
                      onChange={formik.handleChange}
                    />
                  </Form.Group>

                  <Form.Group widths="equal">
                    <Form.Input
                      error={_.has(formik.touched, 'email') ? _.get(formik.errors, 'email') : null}
                      disabled={status === 'paying'}
                      fluid
                      type="text"
                      label={t('email')}
                      name={'email'}
                      placeholder={t('email')}
                      value={formik.values['email']}
                      onChange={formik.handleChange}
                    />
                    <Form.Input
                      error={
                        _.has(formik.touched, 'mobile') ? _.get(formik.errors, 'mobile') : null
                      }
                      disabled={status === 'paying'}
                      fluid
                      type="text"
                      label={t('mobile')}
                      name={'mobile'}
                      placeholder={t('mobile')}
                      value={formik.values['mobile']}
                      onChange={formik.handleChange}
                    />
                  </Form.Group>

                  <Form.TextArea
                    error={
                      _.has(formik.touched, 'address') ? _.get(formik.errors, 'address') : null
                    }
                    disabled={status === 'paying'}
                    type="text"
                    label={t('address')}
                    name={'address'}
                    placeholder={t('address')}
                    value={formik.values['address']}
                    onChange={formik.handleChange}
                  />

                  <Form.Group widths="equal">
                    <Form.Input
                      error={
                        _.has(formik.touched, 'postalCode')
                          ? _.get(formik.errors, 'postalCode')
                          : null
                      }
                      disabled={status === 'paying'}
                      fluid
                      type="text"
                      label={t('postalCode')}
                      name={'postalCode'}
                      placeholder={t('postalCode')}
                      value={formik.values['postalCode']}
                      onChange={formik.handleChange}
                    />
                    <Form.Input
                      error={
                        _.has(formik.touched, 'country') ? _.get(formik.errors, 'country') : null
                      }
                      disabled={status === 'paying'}
                      fluid
                      type="text"
                      label={t('country')}
                      name={'country'}
                      placeholder={t('country')}
                      value={formik.values['country']}
                      onChange={formik.handleChange}
                    />
                    <Form.Input
                      error={_.has(formik.touched, 'city') ? _.get(formik.errors, 'city') : null}
                      disabled={status === 'paying'}
                      fluid
                      type="text"
                      label={t('city')}
                      name={'city'}
                      placeholder={t('city')}
                      value={formik.values['city']}
                      onChange={formik.handleChange}
                    />
                  </Form.Group>
                </Form>
              </div>
            </div>
          ) : null}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button positive content={t('pay')} onClick={pay} />
        <Button negative content={t('cancel')} onClick={props.onCancel} />
      </Modal.Actions>
    </Modal>
  );
};

Invoice.propTypes = {
  open: PropTypes.bool,
  invoice: PropTypes.object,
  onPay: PropTypes.func,
  onCancel: PropTypes.func,
};

export default Invoice;
